import { useState, type ComponentProps } from 'react';
import {
  Button,
  Heading,
  Paragraph,
  Popover,
  PopoverContent,
  PopoverTriggerButton,
} from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { GatedFeaturesCallToAction } from './gated-features-call-to-action';

type GatedFeaturesPopoverProps = ComponentProps<typeof PopoverTriggerButton> & {
  featureName?: string;
  title?: string;
  text?: string;
};

/**
 * An upgrade-prompt popover whose trigger is a button (the remaining props are
 * forwarded to it). Clicking the popover's CTA opens the gated-features
 * confirmation modal. Port of the Ember `gated-features/popover` component.
 */
export function GatedFeaturesPopover({
  featureName,
  title,
  text,
  children,
  ...triggerButtonProps
}: GatedFeaturesPopoverProps) {
  const { account } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  // Essentials-trial accounts are pitched a Premium *trial* rather than a sales
  // conversation (mirrors the Ember popover's `isEssentialsTrial` check).
  const isEssentialsTrial = account.suspend_trial_at != null;

  return (
    <GatedFeaturesCallToAction featureName={featureName}>
      {({ trigger }) => (
        <Popover isOpen={isOpen} onOpenChange={(open) => setIsOpen(open)} shouldTriggerOnClick placement="bottom">
          <PopoverTriggerButton {...triggerButtonProps}>{children}</PopoverTriggerButton>
          <PopoverContent>
            <Heading level="3" mb="050" data-test-gated-features-popover-title>
              {title}
            </Heading>
            <Paragraph mb="200" data-test-gated-features-popover-text>
              {text}
            </Paragraph>
            <Button
              data-test-gated-features-popover-btn
              variant="primary"
              onClick={() => {
                setIsOpen(false);
                trigger();
              }}
            >
              {isEssentialsTrial ? 'Switch to a Premium Trial' : "Let's Talk"}
            </Button>
          </PopoverContent>
        </Popover>
      )}
    </GatedFeaturesCallToAction>
  );
}
